import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import {
    getUnitListPrice,
    getUnitNetPrice,
    getUnitRegularPriceWithVat,
} from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import ProductPriceSavingsMessage from "@insite/content-library/Components/ProductPriceSavingsMessage";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductContextModel | CartLineModel;
    currencySymbol?: string;
    label?: string;
    showLabel?: boolean;
    showUnitOfMeasure?: boolean;
    showPack?: boolean;
    showSavings?: boolean;
    showSavingsAmount?: boolean;
    showSavingsPercent?: boolean;
    showSalePriceLabel?: boolean;
    extendedStyles?: ProductPriceStyles;
    cartLineDiscounted?: string | undefined | boolean | null;
    showInvalidPriceMessage?: boolean;
    productComponentFlag?: boolean | undefined | null;
    OriginalPriceDiv?: any;
    cartlineFlag?: boolean | undefined | null;
    pdpPageFlag?: boolean | undefined | null;
    isCartDiscounted?: boolean | undefined | null;
    saveOrderFlag?: boolean;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    breadcrumbsDetails: state.components.breadcrumbs,
    currencySymbol: ownProps.currencySymbol ?? state.context.session.currency?.currencySymbol ?? "",
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    isGuest: getCurrentUserIsGuest(state),
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
    isAuthenticated: state.context.session.isAuthenticated,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductPriceStyles {
    wrapper?: any;
    priceLabelText?: TypographyPresentationProps;
    quoteMessageWrapper?: InjectableCss;
    quoteMessage?: RequiresQuoteMessageStyle;
    unitOfMeasure?: UnitOfMeasureStyles;
    priceWrapper?: InjectableCss;
    price?: PriceStyles;
    secondaryPriceWrapper?: InjectableCss;
    secondaryPrice?: PriceStyles;
    packWrapper?: InjectableCss;
    packLabelText?: TypographyPresentationProps;
    packDescriptionText?: TypographyPresentationProps;
    savingsMessageText?: TypographyPresentationProps;
    PromotionInterface?: PromotionInterfaceStyles;
}

interface RequiresQuoteMessageStyle {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

interface PriceStyles {
    priceText?: TypographyPresentationProps;
    signInText?: TypographyPresentationProps;
    errorText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    unitOfMeasure?: UnitOfMeasureStyles;
    /**
     * @deprecated Use the `unitOfMeasure.unitOfMeasureText` property instead.
     */
    unitOfMeasureText?: TypographyPresentationProps;
    qtyPerBaseUnitOfMeasureText?: TypographyPresentationProps;
    vatLabelText?: TypographyPresentationProps;
    priceDisWrapper?: InjectableCss;
    PromotionInterface?: PromotionInterfaceStyles;
}

interface UnitOfMeasureStyles {
    unitOfMeasureText?: TypographyPresentationProps;
}

interface PromotionInterfaceStyles {
    promotionText?: TypographyPresentationProps;
    promotionTextSyle?: TypographyPresentationProps;
}

export const productPriceStyles: ProductPriceStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
    priceLabelText: {
        variant: "legend",
    },
    priceWrapper: {
        css: css`
            ${wrapInContainerStyles}
        `,
    },
    price: {
        priceText: { weight: "bold" },
        errorText: { weight: "bold" },
        realTimeText: {
            weight: "bold",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 25px;

                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasure: {
            unitOfMeasureText: {
                css: css`
                    white-space: nowrap;
                `,
            },
        },
        vatLabelText: {
            css: css`
                font-size: 12px;
            `,
        },
        priceDisWrapper: {
            css: css`
                display: flex;
                flex-direction: column;
            `,
        },
    },
    secondaryPriceWrapper: {
        css: css`
            margin-top: 5px;
            ${wrapInContainerStyles}
        `,
    },
    secondaryPrice: {
        priceText: { weight: "bold" },
        errorText: { weight: "bold" },
        realTimeText: {
            weight: "bold",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 25px;

                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasure: {
            unitOfMeasureText: {
                css: css`
                    white-space: nowrap;
                `,
            },
        },
        vatLabelText: {
            css: css`
                font-size: 12px;
            `,
        },
    },
    packWrapper: {
        css: css`
            margin-bottom: 8px;
        `,
    },
    packLabelText: {
        weight: "bold",
    },
    savingsMessageText: {
        italic: true,
        size: "12px",
    },
    quoteMessage: {
        text: { weight: "bold" },
    },
    PromotionInterface: {
        promotionText: {
            css: css`
                color: red;
                font-size: 30px;
                white-space: nowrap;
                font-weight: bold;
                margin-left: 7px;
            `,
        },
        promotionTextSyle: {
            css: css`
                color: red;
            `,
        },
    },
};

const SectionWrapper = getStyledWrapper("section");

const ProductPrice = ({
    product: pricingData,
    currencySymbol,
    canSeePrices,
    showLabel = true,
    label,
    showUnitOfMeasure = true,
    showPack = true,
    showSavings = false,
    showSavingsAmount = false,
    showSavingsPercent = false,
    enableVat,
    vatPriceDisplay,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
    isGuest,
    showSalePriceLabel = true,
    breadcrumbsDetails,
    showInvalidPriceMessage = false,
    cartlineFlag,
    pdpPageFlag = false,
    isAuthenticated,
    isCartDiscounted = false,
    saveOrderFlag = false,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(productPriceStyles, extendedStyles));
    const [promotionValue, setPromotionValue] = React.useState("");
    const [isPromotionPercentageFlag, setIsPromotionPercentageFlag] = React.useState(true)

    if (!showPricing && !isCheckoutReviewAndSubmitPage) {
        return null;
    }

    React.useEffect(() => {
        // Combining all the breadcrumb links children into one array
        const breadcrumbLinks = breadcrumbsDetails?.links;
        const linkArray =
            breadcrumbLinks &&
            breadcrumbLinks.map((item, index) => {
                return item.children;
            });

        // Get the promotion value from the proudct properties
        const propertiesCategory =
            "product" in pricingData
                ? pricingData.product.properties?.promotionCategory
                : pricingData.properties?.promotionCategory;
        if (propertiesCategory) {
            // Get the specific promotion value by checking the products are comes under the specific category
            // const extractingData = propertiesCategory.match(/-?\d+/);
            const extractingData = propertiesCategory.match(/(-?\d+\.\d+)-([True|False]+)/);
            const promotionPrice = extractingData && extractingData.length > 0 ? Math.abs(parseInt(extractingData[1], 10)) : "";
            const isPrecentagePromotion = extractingData && extractingData.length > 0 ? extractingData[2] === 'True' : false;
            const promotionCategoryNameValue = `${promotionPrice.toString()}` || "";
            setIsPromotionPercentageFlag(isPrecentagePromotion);
            setPromotionValue(promotionCategoryNameValue);
        }
    }, []);

    const productContextModel = pricingData as ProductContextModel;
    const cartLineModel = pricingData as CartLineModel;

    const unitOfMeasureDescription =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                ?.description
            : pricingData.unitOfMeasureDescription;
    const unitOfMeasureDisplay =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                ?.unitOfMeasureDisplay
            : pricingData.unitOfMeasureDisplay;
    const quoteRequired = productContextModel.product
        ? productContextModel.product.quoteRequired
        : cartLineModel.quoteRequired;
    const qtyOrdered = productContextModel.product
        ? productContextModel.productInfo.qtyOrdered
        : cartLineModel.qtyOrdered;

    let pricing;
    if ("product" in pricingData) {
        pricing = pricingData.productInfo.pricing;
    } else {
        pricing = pricingData.pricing;
    }

    const unitOfMeasure = unitOfMeasureDescription || unitOfMeasureDisplay;
    const showQtyPerBaseUnitOfMeasure =
        "baseUnitOfMeasure" in pricingData &&
        pricingData.unitOfMeasure !== pricingData.baseUnitOfMeasure &&
        pricingData.qtyPerBaseUnitOfMeasure > 0;
    const packDescription = "product" in pricingData ? pricingData.product.packDescription : undefined;

    const quoteStyles = styles.quoteMessage || {};
    const priceStyles = styles.price || {};
    const secondaryPriceStyles = styles.secondaryPrice || {};
    const promotionPriceStyles = styles.PromotionInterface || {};

    if (!canSeePrices) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...priceStyles.signInText}>{siteMessage("Pricing_SignInForPrice")}</Typography>
            </StyledWrapper>
        );
    }

    if ("product" in pricingData && pricingData.productInfo.failedToLoadPricing) {
        return (
            <SectionWrapper {...styles.wrapper}>
                <Typography {...priceStyles.errorText}>{siteMessage("RealTimePricing_PriceLoadFailed")}</Typography>
            </SectionWrapper>
        );
    }

    if (quoteRequired) {
        return (
            <SectionWrapper {...styles.wrapper}>
                {showLabel && <Typography {...styles.priceLabelText}>{translate("Price")}</Typography>}
                <StyledWrapper {...styles.quoteMessageWrapper}>
                    <Typography {...quoteStyles.text} data-test-selector="quoteRequiredText">
                        {translate("Requires Quote")}
                    </Typography>
                    <Tooltip {...quoteStyles.tooltip} text={siteMessage("Rfq_TooltipMessage").toString()} />
                    <UnitOfMeasure
                        unitOfMeasure={unitOfMeasure}
                        showUnitOfMeasure={showUnitOfMeasure}
                        styles={styles.unitOfMeasure}
                        pricing={pricing}
                        qtyOrdered={qtyOrdered}
                    />
                </StyledWrapper>
            </SectionWrapper>
        );
    }

    const displayVatForPrice = vatPriceDisplay === "DisplayWithVat" || vatPriceDisplay === "DisplayWithAndWithoutVat";
    const showSecondaryPrice = enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat";
    const salePriceLabel = showSalePriceLabel
        ? productContextModel.product?.salePriceLabel || cartLineModel?.salePriceLabel || translate("Price:")
        : "";
    return (
        <SectionWrapper {...styles.wrapper}>
            {showLabel && <Typography {...styles.priceLabelText}>{label || translate("Price")}</Typography>}
            <StyledWrapper {...styles.priceWrapper} data-test-selector="priceIncludeVat">
                <Price
                    currencySymbol={currencySymbol}
                    pricing={pricing}
                    qtyOrdered={qtyOrdered}
                    unitOfMeasure={unitOfMeasure}
                    showUnitOfMeasure={showUnitOfMeasure}
                    enableVat={enableVat}
                    displayWithVat={displayVatForPrice}
                    styles={priceStyles}
                    isGuest={isGuest}
                    salePriceLabel={salePriceLabel}
                    promotionCategoryNameValue={promotionValue}
                    isPromotionPercentageFlag={isPromotionPercentageFlag}
                    cartlineFlag={cartlineFlag}
                    pdpPageFlag={pdpPageFlag}
                    promotionStyle={promotionPriceStyles}
                    isAuthenticated={isAuthenticated}
                    isCartDiscounted={isCartDiscounted}
                    saveOrderFlag={saveOrderFlag}
                />
            </StyledWrapper>
            {showSecondaryPrice && (
                <StyledWrapper {...styles.secondaryPriceWrapper} data-test-selector="priceExcludeVat">
                    <Price
                        currencySymbol={currencySymbol}
                        pricing={pricing}
                        qtyOrdered={qtyOrdered}
                        unitOfMeasure={unitOfMeasure}
                        showUnitOfMeasure={showUnitOfMeasure}
                        enableVat={enableVat}
                        displayWithVat={false}
                        isGuest={isGuest}
                        styles={secondaryPriceStyles}
                        promotionCategoryNameValue={promotionValue}
                        isPromotionPercentageFlag={isPromotionPercentageFlag}
                        cartlineFlag={cartlineFlag}
                        pdpPageFlag={pdpPageFlag}
                        promotionStyle={promotionPriceStyles}
                        isAuthenticated={isAuthenticated}
                        isCartDiscounted={isCartDiscounted}
                        saveOrderFlag={saveOrderFlag}
                    />
                </StyledWrapper>
            )}
            {showQtyPerBaseUnitOfMeasure && "baseUnitOfMeasure" in pricingData && (
                <Typography {...priceStyles.qtyPerBaseUnitOfMeasureText}>
                    {`${pricingData.qtyPerBaseUnitOfMeasure} ${pricingData.baseUnitOfMeasureDisplay} / ${unitOfMeasure}`}
                </Typography>
            )}
            {packDescription && showPack && (
                <StyledWrapper {...styles.packWrapper}>
                    <Typography {...styles.packLabelText}>{translate("Pack")}:&nbsp;</Typography>
                    <Typography {...styles.packDescriptionText}>{packDescription}</Typography>
                </StyledWrapper>
            )}
        </SectionWrapper>
    );
};

interface PriceProps {
    currencySymbol: string;
    pricing: ProductPriceDto | null | undefined;
    qtyOrdered: number | null;
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    enableVat: boolean;
    displayWithVat: boolean;
    styles: PriceStyles;
    salePriceLabel?: string;
    displayPrice?: boolean;
    isGuest?: boolean;
    promotionCategoryNameValue?: string | undefined;
    cartlineFlag?: boolean | undefined | null;
    pdpPageFlag?: boolean | null | undefined;
    promotionStyle?: PromotionInterfaceStyles;
    isAuthenticated?: boolean;
    isCartDiscounted?: boolean | undefined | null;
    saveOrderFlag?: boolean;
    isPromotionPercentageFlag?: boolean;
}

const Price = ({
    currencySymbol,
    pricing,
    qtyOrdered,
    unitOfMeasure,
    showUnitOfMeasure = true,
    enableVat,
    displayWithVat,
    styles,
    salePriceLabel,
    displayPrice = true,
    isGuest,
    promotionCategoryNameValue,
    cartlineFlag,
    pdpPageFlag,
    promotionStyle,
    isAuthenticated,
    isCartDiscounted,
    saveOrderFlag,
    isPromotionPercentageFlag,
}: PriceProps) => {
    let salePriceLabelPrefix = "";
    if (pricing && (salePriceLabel || salePriceLabel === '')) {
        const { price: unitNetPrice } = getUnitNetPrice(pricing, qtyOrdered || 1);
        const { price: unitListPrice } = getUnitListPrice(pricing, qtyOrdered || 1);

        if (getUnitNetPrice(pricing, qtyOrdered || 1).price <= 0) {
            displayPrice = false;
        }

        if (unitNetPrice < unitListPrice) {
            salePriceLabelPrefix = `${salePriceLabel} `;
        }
    }
    return (
        <StyledWrapper {...styles.priceDisWrapper}>
            <StyledWrapper css={cartlineFlag && isCartDiscounted ? css`color: red;` : css`color: #7f7f7f;`}>
                {(pricing && displayPrice) ? (
                    <>
                        <Typography {...styles.priceText} data-test-selector="productPrice_unitNetPrice">
                            {cartlineFlag && isCartDiscounted && promotionCategoryNameValue !== ""
                                ? `${promotionCategoryNameValue}${isPromotionPercentageFlag ? '%' : '$'} Off ${salePriceLabel}`
                                : salePriceLabel}
                            &nbsp;
                            {saveOrderFlag && promotionCategoryNameValue !== ""
                                ? pricing.unitRegularPriceDisplay
                                : enableVat && displayWithVat
                                    ? getUnitRegularPriceWithVat(pricing, qtyOrdered || 1).priceDisplay
                                    : getUnitNetPrice(pricing, qtyOrdered || 1).priceDisplay}
                        </Typography>
                    </>
                ) : (
                    <>

                        <Typography {...styles.priceText} data-test-selector="productPrice_unitNetPrice">
                            {siteMessage("no_pricing")}
                        </Typography>
                    </>
                )}
                {(pricing && displayPrice) && <UnitOfMeasure
                    unitOfMeasure={unitOfMeasure}
                    showUnitOfMeasure={showUnitOfMeasure}
                    styles={styles.unitOfMeasure}
                    pricing={pricing}
                    qtyOrdered={qtyOrdered}
                />}
            </StyledWrapper>

            <PromotionInterface
                unitOfMeasure={unitOfMeasure}
                pricing={pricing}
                qtyOrdered={qtyOrdered}
                promotionCategoryNameValue={promotionCategoryNameValue}
                isPromotionPercentageFlag={isPromotionPercentageFlag}
                cartlineFlag={cartlineFlag}
                pdpPageFlag={pdpPageFlag}
                promotionStyle={promotionStyle}
            />

            {pricing && enableVat && (
                <Typography as="p" {...styles.vatLabelText}>
                    {displayWithVat ? `${translate("Inc. VAT")} (${pricing.vatRate}%)` : translate("Ex. VAT")}
                </Typography>
            )}
        </StyledWrapper>
    );
};

interface UnitOfMeasureProps {
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    styles?: UnitOfMeasureStyles;
    pricing?: ProductPriceDto | null | undefined;
    qtyOrdered?: number | null;
    displayPrice?: boolean;
}

const UnitOfMeasure = ({ unitOfMeasure, showUnitOfMeasure = true, styles, pricing, qtyOrdered, displayPrice = true }: UnitOfMeasureProps) => {
    if (pricing) {
        if (getUnitNetPrice(pricing, qtyOrdered || 1).price <= 0) {
            displayPrice = false;
        }
    }

    if (!pricing && !displayPrice) {
        return null;
    }

    return unitOfMeasure && showUnitOfMeasure ? (
        <>
            <Typography {...styles?.unitOfMeasureText} data-test-selector="productPrice_unitOfMeasureLabel">
                &nbsp;/&nbsp;{unitOfMeasure}
            </Typography>
        </>
    ) : null;
};

interface PromotionInterfaceProps {
    unitOfMeasure?: string;
    pricing?: ProductPriceDto | null | undefined;
    qtyOrdered?: number | null;
    displayPrice?: boolean;
    promotionCategoryNameValue?: string | undefined;
    cartlineFlag?: boolean | undefined | null;
    pdpPageFlag?: boolean | undefined | null;
    promotionStyle?: PromotionInterfaceStyles;
    isPromotionPercentageFlag?: boolean;
}

const PromotionInterface = ({
    unitOfMeasure,
    pricing,
    qtyOrdered,
    displayPrice = true,
    promotionCategoryNameValue,
    cartlineFlag,
    pdpPageFlag,
    promotionStyle,
    isPromotionPercentageFlag,
}: PromotionInterfaceProps) => {
    if (pricing) {
        if (getUnitNetPrice(pricing, qtyOrdered || 1).price <= 0) {
            displayPrice = false;
        }
    }

    if (
        (!pricing && !displayPrice) ||
        cartlineFlag ||
        !promotionCategoryNameValue ||
        promotionCategoryNameValue === "" ||
        !pricing?.unitRegularPrice
    ) {
        return null;
    }

    const splicePromotionValue = promotionCategoryNameValue?.slice(0, promotionCategoryNameValue.length - 1);
    const pricePromotionDiscountValue = isPromotionPercentageFlag ? (
        (Number(pricing?.unitRegularPrice) / 100) *
        Number(promotionCategoryNameValue)
    ).toFixed(2) : ((Number(pricing?.unitRegularPrice)) - Number(promotionCategoryNameValue));
    const productValueafterDiscount = isPromotionPercentageFlag ? (Number(pricing?.unitRegularPrice) - Number(pricePromotionDiscountValue)).toFixed(
        2,
    ) : Number(pricePromotionDiscountValue).toFixed(2);
    return (
        <>
            {pdpPageFlag ? (
                <Typography {...promotionStyle?.promotionText}>
                    <b>${productValueafterDiscount}</b>&nbsp;/&nbsp;{unitOfMeasure} ({promotionCategoryNameValue}{isPromotionPercentageFlag ? '%' : '$'} Off
                    Sale)
                </Typography>
            ) : (
                <Typography {...promotionStyle?.promotionTextSyle}>
                    <b>
                        {promotionCategoryNameValue}{isPromotionPercentageFlag ? '%' : '$'} Off Sale: ${productValueafterDiscount}
                    </b>
                    &nbsp;/&nbsp;{unitOfMeasure}
                </Typography>
            )}

            <br />
        </>
    );
};

export default connect(mapStateToProps)(ProductPrice);
